import React, { useState, useEffect } from 'react';
import { BookOpenIcon, PhotoIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import DocumentUploader from './DocumentUploader';
import DocumentList from './DocumentList';
import SearchTester from './SearchTester';
import KnowledgeStats from './KnowledgeStats';
import TextExtractionReviewer from './TextExtractionReviewer';
import KnowledgeChunkBrowser from './KnowledgeChunkBrowser';
import knowledgeService, { KnowledgeItem } from '../../services/knowledgeService';

const KnowledgeBase: React.FC = () => {
  const [documents, setDocuments] = useState<KnowledgeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState<'documents' | 'extractions' | 'chunks'>('documents');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const data = await knowledgeService.getKnowledgeItems();
      setDocuments(data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    fetchDocuments();
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDocumentDeleted = () => {
    fetchDocuments();
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BookOpenIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Knowledge Base Management</h2>
              <p className="text-gray-600 mt-1">
                Upload and manage documents and images to make your AI chatbot smarter
              </p>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'documents'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <BookOpenIcon className="h-4 w-4 inline mr-2" />
              Documents
            </button>
            <button
              onClick={() => setActiveTab('extractions')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'extractions'
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <PhotoIcon className="h-4 w-4 inline mr-2" />
              Text Extractions
            </button>
            <button
              onClick={() => setActiveTab('chunks')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'chunks'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <DocumentTextIcon className="h-4 w-4 inline mr-2" />
              Chunk Browser
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'documents' ? (
        <>
          {/* Statistics */}
          <KnowledgeStats 
            documentCount={documents.length} 
            refreshTrigger={refreshTrigger}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Upload and Search */}
            <div className="space-y-6">
              <DocumentUploader onUploadSuccess={handleUploadSuccess} />
              <SearchTester />
            </div>

            {/* Right Column - Document List (spans 2 columns on large screens) */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-12 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <DocumentList 
                  documents={documents} 
                  onDocumentDeleted={handleDocumentDeleted}
                />
              )}
            </div>
          </div>
        </>
      ) : activeTab === 'extractions' ? (
        <TextExtractionReviewer />
      ) : (
        <KnowledgeChunkBrowser />
      )}

      {/* Help Section */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">How It Works</h3>
        {activeTab === 'documents' ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-semibold mb-1">1. Upload Files</h4>
              <p>Upload PDF, DOCX, TXT, MD, or image files containing information about your school.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">2. AI Text Extraction</h4>
              <p>Images are processed with AI vision to extract all readable text content.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">3. Smart Chunking</h4>
              <p>Content is split into optimized 600-token chunks for better search accuracy.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">4. Intelligent Responses</h4>
              <p>The AI chatbot uses this knowledge to provide accurate, context-aware answers.</p>
            </div>
          </div>
        ) : activeTab === 'extractions' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-semibold mb-1">1. AI Vision Processing</h4>
              <p>Images are processed using OpenAI's Vision API to extract all visible text with confidence scoring.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">2. Human Review</h4>
              <p>Review and edit AI-extracted text to ensure accuracy before it becomes searchable knowledge.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">3. Automatic Re-embedding</h4>
              <p>Approved text is automatically re-processed into search embeddings for the chatbot to use.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-semibold mb-1">1. View All Knowledge</h4>
              <p>Browse every individual chunk of text that your AI chatbot has access to and can use in conversations.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">2. Manual Control</h4>
              <p>Edit, delete, or add chunks directly. Search for specific content to find duplicates or outdated information.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">3. Instant Updates</h4>
              <p>Changes are automatically re-embedded and immediately available to your chatbot for more accurate responses.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;